import { InjectionToken } from '@angular/core';

/**
 * Platform-agnostic result of a photo capture. `dataUrl` is kept alongside
 * `blob` because the activation flow renders an inline preview from it before
 * upload — see libs/activations/.../ui/photo-step.
 */
export interface CapturedImage {
  readonly dataUrl: string;
  readonly blob: Blob;
}

export interface PhotoCaptureOptions {
  readonly tip?: string;
  readonly quality?: number;
  /**
   * Web-only: DOM element the live camera view mounts into inline, inheriting
   * the page's own layout (the original pre-platform-seam behavior — no
   * button press, no floating overlay). Ignored natively; the OS camera UI
   * owns its own chrome. Required for the web provider — omitting it resolves
   * `null` immediately, same as an explicit dismiss.
   */
  readonly hostElement?: HTMLElement;
}

/**
 * One imperative, `Promise`-returning contract for both platforms (design D2).
 * Resolves `null` ONLY on explicit user dismiss/cancel — never on permission
 * denial or error. There is deliberately no file-picker fallback path on
 * either implementation (ADR-8).
 */
export interface PhotoCapturePort {
  /**
   * Whether `photo-step` should auto-launch `capture()` as soon as the step
   * needs a photo (web — matches the pre-platform-seam auto-start UX), or
   * wait for an explicit user tap on a manual trigger button (native —
   * preserves mobile's pre-migration "Abrir cámara" UX). Design addendum to
   * D2, decided when the auto-start behavior above was restored (commit
   * e5a57922) and would otherwise have silently changed native's UX too.
   */
  readonly autoStart: boolean;
  capture(options?: PhotoCaptureOptions): Promise<CapturedImage | null>;
}

export const PHOTO_CAPTURE = new InjectionToken<PhotoCapturePort>('PHOTO_CAPTURE');
